import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Modal,
  Input,
  Button,
  Checkbox,
  Spin,
  Empty,
  Pagination,
  Space,
  Typography,
  Badge,
  Divider,
  message,
} from "antd";
import {
  AppstoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { store } from "../../redux/configStore";
import { getListServiceProduct } from "../../api/pointPromotion/serviceProduct";
import { neutral, brand, radius, typography, border, spacing } from "../../theme/tokens";

const { Text } = Typography;
const PAGE_SIZE = 20;

const ProductRow = React.memo(function ProductRow({ product, checked, onToggle }) {
  return (
    <div
      onClick={() => onToggle(product)}
      style={{
        padding: `${spacing[3]}px ${spacing[4]}px`,
        border: border.hairline,
        borderColor: checked ? brand.primary : neutral[200],
        borderRadius: radius.base,
        cursor: "pointer",
        backgroundColor: checked ? brand.primarySubtle : neutral[0],
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        transition: "border-color 120ms, background-color 120ms",
      }}
    >
      <Checkbox checked={checked} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: typography.size.base,
            fontWeight: typography.weight.medium,
            display: "block",
            color: neutral[900],
          }}
          ellipsis
        >
          {product.name}
        </Text>
        {product.code && (
          <Text style={{ fontSize: typography.size.xs, color: neutral[500] }}>
            {product.code}
          </Text>
        )}
      </div>
    </div>
  );
});

export default function ServiceProductSelector({
  branchId,
  value = [],
  initialItems = [],
  onChange,
  disabled = false,
  buttonStyle = {},
  // Cho phép điều khiển đóng/mở từ ngoài + ẩn nút trigger mặc định
  // (dùng khi muốn mở bằng nút riêng, vd nút "Thêm sản phẩm" nhỏ).
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (v) => {
    if (openProp === undefined) setOpenState(v);
    onOpenChange?.(v);
  };
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [tempSelected, setTempSelected] = useState(new Map());
  const [viewMode, setViewMode] = useState("all");

  const itemMapRef = useRef(new Map());

  useEffect(() => {
    initialItems.forEach((item) => {
      if (item?.id) itemMapRef.current.set(item.id, item);
    });
  }, [initialItems]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const fetchProducts = useCallback(
    async (p, search) => {
      if (!branchId) return;
      setLoading(true);
      try {
        const response = await store.dispatch(
          getListServiceProduct({
            currentPage: p,
            pageSize: PAGE_SIZE,
            branchId,
            search: search || "",
          })
        );
        const list = response.payload?.data || [];
        setProducts(list);
        setTotal(response.payload?.total || response.payload?.meta?.total || list.length);
        list.forEach((item) => itemMapRef.current.set(item.id, item));
      } catch (e) {
        message.error("Lỗi tải sản phẩm");
      } finally {
        setLoading(false);
      }
    },
    [branchId]
  );

  const debouncedFetch = useMemo(
    () =>
      debounce((p, search) => {
        fetchProducts(p, search);
      }, 400),
    [fetchProducts]
  );

  useEffect(() => {
    if (open && branchId) {
      fetchProducts(page, searchTerm);
    }
  }, [open, page, branchId, fetchProducts, searchTerm]);

  useEffect(() => {
    if (open) {
      const map = new Map();
      value.forEach((id) => {
        const item = itemMapRef.current.get(id) || { id, name: `SP #${id}` };
        map.set(id, item);
      });
      setTempSelected(map);
    }
  }, [open, value]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    setPage(1);
    setSearchTerm(v);
    debouncedFetch(1, v);
  };

  const handleToggle = useCallback((product) => {
    setTempSelected((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.set(product.id, product);
      return next;
    });
  }, []);

  const handleConfirm = () => {
    const ids = Array.from(tempSelected.keys());
    const items = Array.from(tempSelected.values());
    items.forEach((item) => itemMapRef.current.set(item.id, item));
    onChange?.(ids, items);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setViewMode("all");
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
  };

  const handleClearAll = () => setTempSelected(new Map());

  const selectedList = useMemo(() => Array.from(tempSelected.values()), [tempSelected]);

  const renderedItems = viewMode === "selected" ? selectedList : products;

  return (
    <>
      {!hideTrigger && (
        <Button
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
          disabled={disabled || !branchId}
          style={{
            width: "100%",
            height: 56,
            borderRadius: radius.base,
            borderColor: neutral[300],
            background: neutral[0],
            color: neutral[700],
            fontSize: typography.size.md,
            fontWeight: typography.weight.medium,
            ...buttonStyle,
          }}
        >
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Text style={{ color: neutral[900], fontSize: typography.size.md, fontWeight: typography.weight.medium }}>
              {value.length > 0 ? `${value.length} sản phẩm đã chọn` : "Chưa chọn sản phẩm"}
            </Text>
            <Text style={{ fontSize: typography.size.xs, color: neutral[500] }}>
              {disabled || !branchId ? "Chọn đơn vị trước" : "Nhấn để chọn sản phẩm áp dụng"}
            </Text>
          </Space>
        </Button>
      )}

      <Modal
        title={
          <div style={{ fontSize: typography.size.lg, fontWeight: typography.weight.semibold, color: neutral[900], display: "flex", alignItems: "center", gap: spacing[2] }}>
            Chọn sản phẩm áp dụng
            <Badge
              count={tempSelected.size}
              showZero
              style={{ backgroundColor: brand.primary, marginLeft: spacing[2] }}
            />
          </div>
        }
        open={open}
        onCancel={handleCancel}
        width="65%"
        styles={{
          body: { padding: spacing[5], maxHeight: "calc(100vh - 200px)", overflow: "hidden", display: "flex", flexDirection: "column" },
        }}
        footer={[
          <Button key="clear" onClick={handleClearAll} disabled={tempSelected.size === 0} icon={<DeleteOutlined />}>
            Bỏ chọn tất cả
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Hủy
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleConfirm}
          >
            Xác nhận ({tempSelected.size})
          </Button>,
        ]}
      >
        <Space style={{ marginBottom: spacing[3], width: "100%" }}>
          <Input
            placeholder="Tìm theo tên hoặc mã sản phẩm"
            prefix={<SearchOutlined style={{ color: neutral[400] }} />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: 420 }}
            disabled={viewMode === "selected"}
          />
          <Button.Group>
            <Button
              type={viewMode === "all" ? "primary" : "default"}
              onClick={() => setViewMode("all")}
            >
              Tất cả
            </Button>
            <Button
              type={viewMode === "selected" ? "primary" : "default"}
              onClick={() => setViewMode("selected")}
            >
              Đã chọn ({tempSelected.size})
            </Button>
          </Button.Group>
        </Space>

        <div style={{ flex: 1, overflow: "auto", paddingRight: 4 }}>
          {loading && viewMode === "all" ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : renderedItems.length === 0 ? (
            <Empty
              description={viewMode === "selected" ? "Chưa chọn sản phẩm nào" : "Không có sản phẩm"}
              style={{ paddingTop: 40 }}
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 10,
              }}
            >
              {renderedItems.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  checked={tempSelected.has(product.id)}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}
        </div>

        {viewMode === "all" && total > PAGE_SIZE && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <div style={{ textAlign: "center" }}>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
                showTotal={(t, r) => `${r[0]}–${r[1]} / ${t} sản phẩm`}
              />
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
